import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaAward,
  FaUsers,
  FaClock,
  FaShieldAlt,
  FaChartLine,
  FaHeadset
} from 'react-icons/fa';
import './WhyChooseUs.css';

const WhyChooseUs = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const benefits = [
    {
      icon: FaAward,
      title: 'Proven Expertise',
      description: '4+ years of industry experience with a track record of successful projects across diverse industries.',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      icon: FaUsers,
      title: 'Expert Team',
      description: 'Highly skilled professionals passionate about delivering innovative solutions tailored to your needs.',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      icon: FaClock,
      title: 'On-Time Delivery',
      description: 'We value your time and consistently deliver projects within agreed timelines without compromising quality.',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    {
      icon: FaShieldAlt,
      title: 'Security First',
      description: 'Enterprise-grade security measures to protect your data and ensure compliance with industry standards.',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    },
    {
      icon: FaChartLine,
      title: 'Scalable Solutions',
      description: 'Future-proof architectures designed to grow with your business and adapt to changing requirements.',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    },
    {
      icon: FaHeadset,
      title: 'Dedicated Support',
      description: 'Round-the-clock support and maintenance to ensure your systems run smoothly at all times.',
      gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section className="why-choose-us" id="why-choose" ref={ref}>
      <div className="why-choose-container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="section-tag">Why Us</span>
          <h2 className="section-title">Why Choose Us</h2>
          <p className="section-description">
            What sets us apart from the competition
          </p>
        </motion.div>

        <motion.div
          className="benefits-grid"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              className="benefit-card"
              variants={itemVariants}
              whileHover={{ y: -10 }}
            >
              <div className="benefit-icon-wrapper">
                <div
                  className="benefit-icon-bg"
                  style={{ background: benefit.gradient }}
                ></div>
                <motion.div
                  className="benefit-icon"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <benefit.icon />
                </motion.div>
              </div>

              <h3>{benefit.title}</h3>
              <p>{benefit.description}</p>

              <div
                className="card-gradient"
                style={{ background: benefit.gradient }}
              ></div>
            </motion.div>
          ))}
        </motion.div>

               <motion.div
                 className="why-choose-footer"
                 initial={{ opacity: 0, y: 30 }}
                 animate={inView ? { opacity: 1, y: 0 } : {}}
                 transition={{ duration: 0.6, delay: 0.8 }}
                 style={{marginTop:'2rem'}}
               >
                 <div className="footer-stats">
                   <div className="footer-stat">
                     <h4>30+</h4>
                     <p>Successful Projects</p>
                   </div>
                   <div className="footer-stat">
                     <h4>98%</h4>
                     <p>Client Retention</p>
                   </div>
                   <div className="footer-stat">
                     <h4>5+</h4>
                     <p>Countries Served</p>
                   </div>
                   <div className="footer-stat">
                     <h4>99%</h4>
                     <p>Client Satisfaction</p>
                   </div>
                 </div>
               </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
